import React, { useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowRight, Printer, RefreshCw } from "lucide-react";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { CHECKLISTS } from "@/data/content";

const TABS = [
  { key: "wedding", label: "Wedding" },
  { key: "corporate", label: "Corporate" },
  { key: "social", label: "Social Celebration" },
  { key: "devotional", label: "Devotional Event" },
];

export default function Checklist() {
  const [active, setActive] = useState("wedding");
  const items = CHECKLISTS[active];
  const [checked, setChecked] = useState({});

  const progress = useMemo(() => {
    const tot = items.length;
    const done = items.filter((i) => checked[`${active}:${i}`]).length;
    return Math.round((done / tot) * 100);
  }, [items, checked, active]);

  const toggle = (it) => setChecked((c) => ({ ...c, [`${active}:${it}`]: !c[`${active}:${it}`] }));
  const reset = () => setChecked((c) => {
    const n = { ...c };
    items.forEach((i) => delete n[`${active}:${i}`]);
    return n;
  });

  return (
    <div data-testid="page-checklist" className="bg-[#FAF6F1]">
      
      <SEO title="Interactive Event Checklist — Wedding, Corporate, Devotional" description="Interactive planning checklist for weddings, corporate events, social celebrations, and devotional events. Track progress, print, and plan with calm precision." path="/checklist" />
<section className="pt-28 md:pt-32 pb-10 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <p className="pill">Event Checklist</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D] max-w-4xl">
            A calmer, clearer way to <span className="italic text-[#B65980]">plan</span>.
          </h1>
          <p className="mt-7 max-w-2xl text-[16.5px] text-[#3B312D]/80 leading-relaxed">
            Track every step from first idea to final execution. Switch between wedding, corporate, social,
            and devotional event checklists below — your progress saves locally for the session.
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-center gap-3 mb-8">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`checklist-tab-${t.key}`}
                className={`px-5 py-2.5 rounded-full text-[12px] tracking-[0.14em] uppercase border transition font-semibold ${
                  active === t.key
                    ? "bg-[#3B312D] text-[#FAF6F1] border-[#3B312D]"
                    : "bg-white text-[#3B312D] border-[rgba(59,49,45,0.14)] hover:border-[#B65980]"
                }`}
              >
                {t.label}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-3">
              <button onClick={reset} data-testid="checklist-reset" className="btn-ghost !py-2 !px-4 !text-[11px]"><RefreshCw size={13}/> Reset</button>
              <button onClick={() => window.print()} data-testid="checklist-print" className="btn-ghost !py-2 !px-4 !text-[11px]"><Printer size={13}/> Print</button>
            </div>
          </div>

          <div className="lux-card p-6 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-[12px] tracking-[0.2em] uppercase text-[#3B312D] font-semibold">Progress</p>
              <p className="font-display text-2xl text-[#B65980]">{progress}%</p>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-[rgba(59,49,45,0.08)] overflow-hidden">
              <div className="h-full bg-[#8A9A7B] transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="lux-card p-6 md:p-10">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {items.map((it, i) => {
                const id = `${active}:${it}`;
                const isOn = !!checked[id];
                return (
                  <li key={it}>
                    <label className="flex items-start gap-4 cursor-pointer group" data-testid={`checklist-item-${active}-${i}`}>
                      <span className={`mt-1 inline-flex w-6 h-6 rounded-full border-2 items-center justify-center transition shrink-0 ${isOn ? "bg-[#B65980] border-[#B65980]" : "border-[#3B312D]/25 group-hover:border-[#B65980]"}`}>
                        {isOn && <span className="text-white text-xs">✓</span>}
                      </span>
                      <span className={`text-[15px] leading-relaxed ${isOn ? "text-[#3B312D]/45 line-through" : "text-[#3B312D]"}`}>{it}</span>
                      <input type="checkbox" className="sr-only" checked={isOn} onChange={() => toggle(it)} />
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary" data-testid="checklist-cta">Get a personalised plan <ArrowRight size={14} /></Link>
            <Link to="/packages" className="btn-ghost">View Packages</Link>
          </div>
        </div>
      </section>

      <BrochureCTA />
      <FinalCTA />
    </div>
  );
}
